import { useState } from 'react';
import { Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SOFT_BADGE_CLASSES,
  useConfirm,
  useToast,
} from '@mustauto/ui/web';
import {
  PACKAGE_DESCRIPTION_MAX,
  PACKAGE_DURATION_HOURS_MAX,
  PACKAGE_INCLUDED_ITEMS_MAX,
  PACKAGE_MODULE,
  PACKAGE_WARRANTY_MONTHS_MAX,
  VEHICLE_SIZES,
  VEHICLE_SIZE_LABELS,
  type PackageSizePrices,
  type VehicleSize,
} from '@mustauto/types';
import {
  consumablesFromDrafts,
  draftFromConsumable,
  formatApiError,
  formatPackageHours,
  formatStockQuantity,
  formatWarrantyMonths,
  hasInvalidConsumableDraft,
  packagePriceForSize,
  type ConsumableDraft,
} from '@mustauto/utils';
import { PackageConsumablesField } from './PackageConsumablesField';
import { formatReais } from '@/lib/utils';
import { useLaborPrices, type LaborPackageFields, type LaborPrice } from '@/hooks/useSettings';

const INPUT = 'rounded-md border border-input bg-background px-3 py-2 text-sm';

/**
 * Pacotes de estética (Fase 2) na aba Mão de Obra. Mesma tabela dos serviços
 * (`labor_prices`, `module = 'estetica'` — decisão A7), cartão próprio: o eixo
 * de preço é o PORTE do veículo, e o pacote tem duração, garantia e itens
 * inclusos. O catálogo padrão é semeado pela API na primeira leitura.
 */
export function EstheticPackagesCard({ packages }: { packages: LaborPrice[] }) {
  const { create, update, remove, isCreating, confirmPackages, isConfirmingPackages } = useLaborPrices();
  const pending = packages.filter((p) => p.pendingConfirmation);
  const { toast } = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<LaborPrice | 'new' | null>(null);

  const handleSave = async (data: PackageFormData) => {
    try {
      if (editing === 'new') await create(data);
      else if (editing) await update({ id: editing.id, ...data });
      toast({ type: 'success', title: editing === 'new' ? 'Pacote cadastrado' : 'Pacote atualizado' });
      setEditing(null);
    } catch (err) {
      toast({
        type: 'error',
        title: 'Falha ao salvar o pacote',
        description: formatApiError(err, 'Verifique os campos e tente novamente.'),
      });
    }
  };

  const handleConfirmAll = async () => {
    const ok = await confirm({
      title: `Confirmar ${pending.length === 1 ? 'o preço sugerido' : `os ${pending.length} preços sugeridos`}?`,
      description: 'Os pacotes passam a valer como estão na tabela, e o mecânico já consegue usá-los na abertura da OS.',
      confirmLabel: 'Confirmar preços',
    });
    if (!ok) return;
    try {
      const res = await confirmPackages();
      toast({ type: 'success', title: res.confirmed === 1 ? '1 preço confirmado' : `${res.confirmed} preços confirmados` });
    } catch (err) {
      toast({ type: 'error', title: 'Falha ao confirmar', description: formatApiError(err, 'Tente novamente em instantes.') });
    }
  };

  const handleDelete = async (p: LaborPrice) => {
    const ok = await confirm({
      title: `Excluir "${p.serviceName}"?`,
      description: 'O pacote sai da lista da abertura de OS. As OS já abertas não mudam.',
      confirmLabel: 'Excluir',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await remove(p.id);
      toast({ type: 'success', title: 'Pacote removido' });
    } catch (err) {
      toast({ type: 'error', title: 'Falha ao remover', description: formatApiError(err, 'Tente novamente em instantes.') });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-foreground">
            <Sparkles size={16} aria-hidden />
            Pacotes de estética
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            O mecânico escolhe o pacote e o porte ao abrir a OS, e o preço já entra pronto. Os pacotes padrão vêm
            com preços de mercado — confira e ajuste aos da oficina.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing(editing === 'new' ? null : 'new')}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} aria-hidden />
          {editing === 'new' ? 'Cancelar' : 'Novo pacote'}
        </button>
      </CardHeader>
      <CardContent className="space-y-4">
        {pending.length > 0 && (
          <div
            role="status"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100"
          >
            <span>
              {pending.length === 1 ? '1 pacote está' : `${pending.length} pacotes estão`} com o preço sugerido. Confira e
              ajuste: o mecânico só consegue usar o pacote depois que o preço é confirmado. Salvar um pacote já confirma o
              preço dele.
            </span>
            <button
              type="button"
              onClick={() => void handleConfirmAll()}
              disabled={isConfirmingPackages}
              className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-800 dark:bg-transparent dark:text-amber-100 dark:hover:bg-amber-900/40"
            >
              {isConfirmingPackages ? 'Confirmando...' : 'Confirmar todos como estão'}
            </button>
          </div>
        )}
        {editing && (
          <PackageForm
            key={editing === 'new' ? 'new' : editing.id}
            initial={editing === 'new' ? null : editing}
            saving={isCreating}
            onSubmit={handleSave}
            onCancel={() => setEditing(null)}
          />
        )}
        {packages.length === 0 ? (
          <EmptyState
            icon={<Sparkles size={32} />}
            title="Nenhum pacote"
            description="Cadastre os pacotes que a oficina vende: vitrificação, polimento, higienização…"
            action={{ label: 'Adicionar pacote', onClick: () => setEditing('new') }}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pacote</TableHead>
                  {VEHICLE_SIZES.map((size) => (
                    <TableHead key={size} className="text-right">
                      {VEHICLE_SIZE_LABELS[size]}
                    </TableHead>
                  ))}
                  <TableHead>Duração</TableHead>
                  <TableHead>Garantia</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {packages.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <span className="font-medium">{p.serviceName}</span>
                      {p.pendingConfirmation && (
                        <span className={`ml-2 rounded-md px-1.5 py-0.5 text-xs font-medium ${SOFT_BADGE_CLASSES.amber}`}>
                          Preço a confirmar
                        </span>
                      )}
                      {p.includedItems && p.includedItems.length > 0 && (
                        <span className="block max-w-xs truncate text-xs text-muted-foreground">
                          {p.includedItems.join(' · ')}
                        </span>
                      )}
                      {p.consumables && p.consumables.length > 0 && (
                        <span className="block max-w-xs truncate text-xs text-muted-foreground">
                          Insumos: {p.consumables.map((c) => `${formatStockQuantity(c.quantity, c.unit)} ${c.name}`).join(' · ')}
                        </span>
                      )}
                    </TableCell>
                    {VEHICLE_SIZES.map((size) => (
                      <TableCell key={size} className="text-right tabular-nums">
                        {formatReais(packagePriceForSize(p.basePrice, p.sizePrices, size))}
                      </TableCell>
                    ))}
                    <TableCell className="text-sm">{p.durationHours ? formatPackageHours(p.durationHours) : '—'}</TableCell>
                    <TableCell className="text-sm">
                      {p.warrantyMonths ? formatWarrantyMonths(p.warrantyMonths) : 'Sem garantia'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => setEditing(p)}
                          aria-label={`Editar ${p.serviceName}`}
                          className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(p)}
                          aria-label={`Excluir ${p.serviceName}`}
                          className="rounded-md border border-border p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type PackageFormData = { serviceName: string; basePrice: number } & LaborPackageFields;

function numberOrNull(text: string, max: number): number | null {
  const n = Number(text.replace(',', '.'));
  if (!text.trim() || !Number.isFinite(n) || n <= 0) return null;
  return Math.min(n, max);
}

function PackageForm({
  initial,
  saving,
  onSubmit,
  onCancel,
}: {
  initial: LaborPrice | null;
  saving: boolean;
  onSubmit: (data: PackageFormData) => Promise<void>;
  onCancel: () => void;
}) {
  const [serviceName, setServiceName] = useState(initial?.serviceName ?? '');
  const [basePrice, setBasePrice] = useState(initial ? String(initial.basePrice) : '');
  const [sizes, setSizes] = useState<Record<VehicleSize, string>>(() => ({
    hatch: initial?.sizePrices?.hatch != null ? String(initial.sizePrices.hatch) : '',
    sedan: initial?.sizePrices?.sedan != null ? String(initial.sizePrices.sedan) : '',
    suv: initial?.sizePrices?.suv != null ? String(initial.sizePrices.suv) : '',
    pickup: initial?.sizePrices?.pickup != null ? String(initial.sizePrices.pickup) : '',
  }));
  const [hours, setHours] = useState(initial?.durationHours ? String(initial.durationHours) : '');
  const [warranty, setWarranty] = useState(initial?.warrantyMonths ? String(initial.warrantyMonths) : '');
  const [items, setItems] = useState((initial?.includedItems ?? []).join('\n'));
  const [description, setDescription] = useState(initial?.description ?? '');
  const [consumables, setConsumables] = useState<ConsumableDraft[]>(() =>
    (initial?.consumables ?? []).map((c) => draftFromConsumable(c)),
  );
  const [submitting, setSubmitting] = useState(false);

  const base = Number(basePrice);
  const warrantyMonths = numberOrNull(warranty, PACKAGE_WARRANTY_MONTHS_MAX);
  const canSubmit =
    serviceName.trim().length > 0 && Number.isFinite(base) && base > 0 && !hasInvalidConsumableDraft(consumables);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const sizePrices: PackageSizePrices = {};
    for (const size of VEHICLE_SIZES) {
      const v = numberOrNull(sizes[size], Number.MAX_SAFE_INTEGER);
      if (v != null) sizePrices[size] = Math.round(v * 100) / 100;
    }
    const hoursValue = numberOrNull(hours, PACKAGE_DURATION_HOURS_MAX);
    setSubmitting(true);
    try {
      await onSubmit({
        serviceName: serviceName.trim(),
        basePrice: Math.round(base * 100) / 100,
        module: PACKAGE_MODULE,
        description: description.trim() || null,
        durationHours: hoursValue != null ? Math.round(hoursValue * 10) / 10 : null,
        warrantyMonths: warrantyMonths != null ? Math.round(warrantyMonths) : null,
        includedItems: items
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
          .slice(0, PACKAGE_INCLUDED_ITEMS_MAX),
        sizePrices: Object.keys(sizePrices).length > 0 ? sizePrices : null,
        consumables: consumablesFromDrafts(consumables),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-lg border p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Pacote *</span>
          <input value={serviceName} onChange={(e) => setServiceName(e.target.value)} maxLength={200} required placeholder="Vitrificação de pintura" className={INPUT} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Preço base (R$) *</span>
          <input type="number" min={0} step="0.01" value={basePrice} onChange={(e) => setBasePrice(e.target.value)} required placeholder="0,00" className={INPUT} />
          <span className="text-xs text-muted-foreground">Vale para o porte sem preço próprio.</span>
        </label>
      </div>
      <fieldset>
        <legend className="mb-1 text-xs font-medium text-muted-foreground">Preço por porte (R$) — vazio = preço base</legend>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {VEHICLE_SIZES.map((size) => (
            <label key={size} className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{VEHICLE_SIZE_LABELS[size]}</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={sizes[size]}
                onChange={(e) => setSizes((prev) => ({ ...prev, [size]: e.target.value }))}
                placeholder={base > 0 ? String(base) : '0,00'}
                className={INPUT}
              />
            </label>
          ))}
        </div>
      </fieldset>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Duração (horas)</span>
          <input type="number" min={0} step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="24" className={INPUT} />
          <span className="text-xs text-muted-foreground">Vira o prazo sugerido na abertura da OS.</span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Garantia (meses)</span>
          <input type="number" min={0} max={PACKAGE_WARRANTY_MONTHS_MAX} step="1" value={warranty} onChange={(e) => setWarranty(e.target.value)} placeholder="Sem garantia" className={INPUT} />
          <span className="text-xs text-muted-foreground">
            {warrantyMonths && warrantyMonths >= 6
              ? 'O cliente recebe o lembrete de revisão aos 6 e aos 12 meses.'
              : 'Com 6 meses ou mais, o cliente recebe o lembrete de revisão.'}
          </span>
        </label>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">O que inclui (um por linha)</span>
          <textarea rows={4} value={items} onChange={(e) => setItems(e.target.value)} placeholder={'Lavagem técnica\nPolimento de 1 etapa'} className={INPUT} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Descrição</span>
          <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={PACKAGE_DESCRIPTION_MAX} className={INPUT} />
        </label>
      </div>
      <PackageConsumablesField value={consumables} onChange={setConsumables} />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit || submitting || saving}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {submitting ? 'Salvando...' : initial?.pendingConfirmation ? 'Confirmar e salvar' : 'Salvar pacote'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
          Cancelar
        </button>
      </div>
    </form>
  );
}
